import React, { useState } from 'react';
import { checkPatientStatus, getPatientAppointments, cancelAppointment } from '../../utils/apis';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import styles from './ManageAppointmentStep.module.css';
import { axisBranding } from './branding';

interface ManageAppointmentStepProps {
  onBack: () => void;
  onDone: () => void;
  onEdit?: (appointment: Appointment) => void;
}

interface Appointment {
  appointmentid: string;
  patientid: string;
  providerid: string;
  departmentid: string;
  appointmenttypeid: string;
  scheduleddatetime: string;
  date: string;
  starttime: string;
  duration: number;
  appointmenttype: string;
  appointmentstatus: string;
  patientappointmenttypename?: string;
  providerDetails?: {
    providerid: number;
    firstname: string;
    lastname: string;
    displayname: string;
    schedulingname: string;
    specialty: string;
    entitytype: string;
    providertype: string;
    billable: boolean;
    status: string;
  };
  // Legacy fields for compatibility
  athenaAppointmentId?: string;
  athenaPatientId?: string;
  athenaProviderId?: string;
  athenaDepartmentId?: string;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  reason?: string;
  reasonType?: string;
  status?: string;
  providerName?: string;
  departmentName?: string;
}

// Helper to format appointment date/time
function formatDateTime(appointment: Appointment): Date {
  try {
    // Prioritize separate date and starttime fields from API response
    if (appointment.date && appointment.starttime) {
      const dateStr = appointment.date; // "09/15/2025"
      const timeStr = appointment.starttime; // "09:00"
      const [month, day, year] = dateStr.split('/');
      const [hour, minute] = timeStr.split(':');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
    }
    
    // Fallback to scheduleddatetime if separate fields are not available
    if (appointment.scheduleddatetime) {
      const date = new Date(appointment.scheduleddatetime);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    // Final fallback
    return new Date();
    } catch (error) {
      // Fallback to current date if parsing fails
      return new Date();
    }
}

const ManageAppointmentStep: React.FC<ManageAppointmentStepProps> = ({ onBack, onDone, onEdit }) => {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [checked, setChecked] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(null);
    setAppointment(null);
    setChecked(false);
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    setLoading(true);
    try {
      // First, check patient status to get Athena patient ID
      const patientStatus = await checkPatientStatus({
        firstName: '', // We only have email, so we'll use empty strings for other fields
        lastName: '',
        dateOfBirth: '',
        email: email
      });

      if (!patientStatus.existingPatient || !patientStatus.athenaPatientId) {
        setError('No existing patient found with this email address.');
        setChecked(true);
        return;
      }

      // Get appointments from Athena using the patient ID
      const appointments = await getPatientAppointments(patientStatus.athenaPatientId);
      
      // Find the first future appointment (status 'f' = confirmed and date in future)
      const now = new Date();
      const future = appointments.find((a: Appointment) => {
        const appointmentDate = formatDateTime(a);
        return (a.appointmentstatus === 'f' || a.appointmentstatus === 'CONFIRMED') &&
               appointmentDate > now;
      });
      
      setAppointment(future || null);
      setChecked(true);
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'finding appointment');
      setError(userFriendlyError);
      setChecked(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!appointment) return;
    
    setCancelling(true);
    try {
      // Use the Athena API for cancellation
      await cancelAppointment({
        appointmentId: appointment.appointmentid,
        patientId: appointment.patientid,
        departmentId: appointment.departmentid,
        providerId: appointment.providerid,
        reason: cancellationReason || 'Patient requested cancellation'
      });
      
      // Update the appointment status locally
      setAppointment({
        ...appointment,
        appointmentstatus: 'CANCELLED'
      });
      setShowCancelConfirm(false);
      setCancellationReason('');
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'cancelling appointment');
      setError(userFriendlyError);
      setShowCancelConfirm(false); // Close the cancel confirmation modal first
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className={styles.manageAppointment}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <span className={styles.navPhone}>{axisBranding.phoneNumber}</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>BOOK ONLINE</button>
        </div>
        <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.manageCard}>
          <div className={styles.cardHeader}>
            <svg className={styles.cardIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
              <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2"/>
              <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2"/>
              <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div className={styles.cardTitle}>Manage Existing Appointment</div>
          </div>
          
          <div className={styles.cardBody}>
            <p className={styles.cardDescription}>
              Enter the email you used to book your appointment. We'll look up your upcoming appointment details.
            </p>
            
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Email Address <span className={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  className={styles.formInput}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="Enter your email"
                />
                {touched && !/^\S+@\S+\.\S+$/.test(email) && (
                  <div className={styles.formError}>Please enter a valid email address.</div>
                )}
              </div>
              
              <button
                className={styles.submitButton}
                type="submit"
                disabled={!/^\S+@\S+\.\S+$/.test(email) || loading}
              >
                {loading ? 'Checking...' : 'Find Appointment'}
              </button>
            </form>
            
            {checked && (
              <div className={styles.resultSection}>
                {error ? (
                  <div className={styles.errorMessage}>{error}</div>
                ) : appointment ? (
                  <div className={styles.appointmentDetails}>
                    <div className={styles.detailTitle}>Upcoming Appointment:</div>
                    <div className={styles.detailGrid}>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Provider:</span>
                        <span className={styles.detailValue}>
                          {appointment.providerDetails?.displayname || 
                           appointment.providerDetails?.firstname + ' ' + appointment.providerDetails?.lastname ||
                           appointment.providerName || 
                           'Unknown Provider'}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Type:</span>
                        <span className={styles.detailValue}>
                          {appointment.patientappointmenttypename || appointment.appointmenttype || 'Unknown Type'}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Date & Time:</span>
                        <span className={styles.detailValue}>
                          {formatDateTime(appointment).toLocaleString([], { 
                            dateStyle: 'medium', 
                            timeStyle: 'short' 
                          })}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Status:</span>
                        <span className={styles.detailValue}>
                          {appointment.appointmentstatus === 'f' ? 'CONFIRMED' : 
                           appointment.appointmentstatus === 'C' ? 'CANCELLED' :
                           appointment.appointmentstatus || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    
                    {appointment.appointmentstatus !== 'C' && appointment.appointmentstatus !== 'CANCELLED' && (
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          onClick={() => onEdit && onEdit(appointment)}
                        >
                          Edit Appointment
                        </button>
                        <button
                          className={styles.cancelButton}
                          onClick={() => setShowCancelConfirm(true)}
                        >
                          Cancel Appointment
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.noAppointment}>
                    No future appointment exists for this email.
                  </div>
                )}
                
                <button
                  className={styles.returnButton}
                  onClick={onDone}
                >
                  Return to Home
                </button>
              </div>
            )}
          </div>
        </div>

        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
            <span>Phone (appointments): {axisBranding.phoneNumber}</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Cancel Appointment</h3>
            <p className={styles.modalText}>
              Are you sure you want to cancel your appointment with {appointment?.providerDetails?.displayname || 
                appointment?.providerDetails?.firstname + ' ' + appointment?.providerDetails?.lastname ||
                appointment?.providerName || 'your provider'}? 
              This action cannot be undone.
            </p>
            
            <div className={styles.reasonSection}>
              <label className={styles.reasonLabel}>
                Reason for cancellation (optional):
              </label>
              <textarea
                className={styles.reasonInput}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please provide a reason for cancelling this appointment..."
                rows={3}
                maxLength={500}
              />
            </div>
            
            <div className={styles.modalButtons}>
              <button
                className={styles.modalButtonSecondary}
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancellationReason('');
                }}
                disabled={cancelling}
              >
                Keep Appointment
              </button>
              <button
                className={styles.modalButtonPrimary}
                onClick={handleCancelAppointment}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAppointmentStep; 